import { useState } from 'react';
import { Alert, Button, Modal, Table, TableProps, Typography, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import {
  LegalAcceptanceHistoryRow,
  LegalDocumentType,
  downloadAcceptedVersion,
} from '@/api/legalDocuments';

interface Props {
  open: boolean;
  onClose: () => void;
  documentType: LegalDocumentType;
  rows: LegalAcceptanceHistoryRow[];
  // Franchisor profiles have multiple users, so the modal shows who accepted;
  // consultant profiles are a single user, so the column is hidden (per Figma).
  showUserColumn: boolean;
  loading?: boolean;
  isError?: boolean;
}

const DOCUMENT_LABEL: Record<LegalDocumentType, string> = {
  terms: 'Terms & Conditions',
  privacy: 'Privacy Policy',
};

// "Apr 12, 2025, 11:31 AM" — date + time, matching the Figma audit-trail rows.
const formatDateTime = (value?: string | null): string => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const StatusPill = ({ status }: { status: 'accepted' | 'pending' }) => {
  const color = status === 'accepted' ? '#1C8271' : '#FFA500';
  const label = status === 'accepted' ? 'Accepted' : 'Pending';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      <Typography.Text>{label}</Typography.Text>
    </div>
  );
};

export default function LegalAcceptanceHistoryModal({
  open,
  onClose,
  documentType,
  rows,
  showUserColumn,
  loading,
  isError,
}: Props): JSX.Element {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (row: LegalAcceptanceHistoryRow) => {
    if (!row.acceptanceId) return;
    setDownloadingId(row.acceptanceId);
    try {
      const { file } = await downloadAcceptedVersion(row.acceptanceId);
      if (!file || typeof file !== 'string') {
        message.error('Download failed: server returned an invalid file.');
        return;
      }
      // The acceptance record is a PDF (binary). Decode base64 into bytes —
      // `new Blob([atob(file)])` would corrupt the binary, so go via Uint8Array.
      const bytes = Uint8Array.from(atob(file), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `legal-acceptance-${row.documentType}-v${row.version}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error: any) {
      // Prefer the NestJS error message (error.response.data.message) over
      // axios's generic "Request failed with status code …".
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          'Download failed. Please try again.',
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const columns: TableProps<LegalAcceptanceHistoryRow>['columns'] = [
    {
      title: 'Document Type',
      dataIndex: 'documentType',
      key: 'documentType',
      render: (dt: LegalDocumentType) => DOCUMENT_LABEL[dt] ?? dt,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      render: (v: number) => `v${v}`,
    },
    {
      title: 'Accepted Date',
      dataIndex: 'acceptedAt',
      key: 'acceptedAt',
      render: (d: string | null) => formatDateTime(d),
    },
    ...(showUserColumn
      ? [
          {
            title: 'User',
            dataIndex: 'userName',
            key: 'userName',
            render: (name: string | null) => name || 'N/A',
          },
        ]
      : []),
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: (ip: string | null) => ip || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: 'accepted' | 'pending') => <StatusPill status={status} />,
    },
    {
      title: 'Download',
      key: 'download',
      render: (_: unknown, row: LegalAcceptanceHistoryRow) =>
        row.status === 'accepted' && row.acceptanceId ? (
          <Button
            type="text"
            icon={<DownloadOutlined />}
            style={{ color: '#1C8271' }}
            loading={downloadingId === row.acceptanceId}
            onClick={() => handleDownload(row)}
          />
        ) : (
          'N/A'
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={860}
      centered
      footer={null}
      // Remount the table on each open so no row state can carry over between
      // the Terms and Privacy views (belt-and-suspenders with the unique key).
      destroyOnClose
      title={
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Legal Acceptance History
          </Typography.Title>
          <Typography.Text type="secondary">
            {DOCUMENT_LABEL[documentType]} · Audit trail
          </Typography.Text>
        </div>
      }
    >
      {isError ? (
        <Alert
          type="error"
          showIcon
          message="Couldn't load the acceptance history"
          description="Something went wrong fetching this profile's legal acceptance records. Please close the dialog and try again."
        />
      ) : (
        <Table
          columns={columns}
          dataSource={rows}
          rowKey={(r, index) => r.acceptanceId ?? `row-${index}`}
          loading={loading}
          pagination={false}
          size="middle"
          scroll={{ x: true }}
          locale={{ emptyText: 'No legal documents published yet' }}
        />
      )}
    </Modal>
  );
}
